import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  CalendarIcon,
  Check,
  ChevronsUpDown,
  Image as ImageIcon,
  ImageOff,
  Images,
  Plus,
  Trash2,
} from 'lucide-react';
import { useFieldArray, useForm, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MediaPickerDialog, type MediaPickerSelection } from '@/features/media';
import { useDebounce } from '@/hooks/use-debounce';
import { getApiErrorMessage } from '@/lib/api/error';
import { cn } from '@/lib/utils';
import { createDeal, patchDeal } from './service/deals.service';
import {
  branchesQueryOptions,
  dealProductsQueryOptions,
  dealsQueryKeyPrefix,
  dealsQueryOptions,
} from './service/query-options';
import type { Deal, DealCreateInput, DealPatchInput } from './service/types';
import { DealModerationBanner } from './components/DealModerationNotice';
import { datetimeLocalToManilaIso, manilaIsoToDatetimeLocal } from './utils/datetime';
import {
  MAX_DEAL_MEDIA,
  buildDealMediaSchema,
  dealMediaToRows,
  rowsToMediaPayload,
} from './utils/media';
import { resolveCreateStatus } from './utils/schedule';

const dealTypeSchema = z.enum(['percent_off', 'fixed_price', 'bundle']);

function buildDealEditorFormSchema(t: TFunction) {
  return z
    .object({
      title: z
        .string()
        .trim()
        .min(1, t('deals.editor.err_title_required'))
        .max(120, t('deals.editor.err_title_long')),
      description: z.string().max(1000, t('deals.editor.err_description_long')),
      media: buildDealMediaSchema(t),
      deal_type: dealTypeSchema,
      discount_value: z.string(),
      fixed_price_centavos: z.string(),
      branch_ids: z.array(z.string()).min(1, t('deals.editor.err_pick_branch')),
      starts_at: z.string(),
      ends_at: z.string(),
      product_id: z.string(),
      total_max_uses: z.string(),
      max_uses_per_consumer: z.string(),
    })
    .superRefine((values, context) => {
      if (values.deal_type === 'percent_off') {
        const discount = Number(values.discount_value);
        if (
          !values.discount_value.trim() ||
          !Number.isFinite(discount) ||
          discount <= 0 ||
          discount > 100
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['discount_value'],
            message: t('deals.editor.err_percent_range'),
          });
        }
      }

      if (values.deal_type === 'fixed_price') {
        const fixedPrice = Number(values.fixed_price_centavos);
        if (
          !values.fixed_price_centavos.trim() ||
          !Number.isFinite(fixedPrice) ||
          fixedPrice < 0 ||
          !Number.isInteger(fixedPrice)
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fixed_price_centavos'],
            message: t('deals.editor.err_fixed_price'),
          });
        }
      }

      const startsAtIso = values.starts_at ? datetimeLocalToManilaIso(values.starts_at) : null;
      const endsAtIso = values.ends_at ? datetimeLocalToManilaIso(values.ends_at) : null;
      if (values.starts_at && !startsAtIso) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['starts_at'],
          message: t('deals.editor.err_start_invalid'),
        });
      }
      if (values.ends_at && !endsAtIso) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ends_at'],
          message: t('deals.editor.err_end_invalid'),
        });
      }

      if (startsAtIso && endsAtIso) {
        const startsAtMillis = new Date(startsAtIso).getTime();
        const endsAtMillis = new Date(endsAtIso).getTime();
        if (
          Number.isFinite(startsAtMillis) &&
          Number.isFinite(endsAtMillis) &&
          endsAtMillis <= startsAtMillis
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ends_at'],
            message: t('deals.editor.err_end_after_start'),
          });
        }
      }
    });
}

type DealEditorFormValues = z.infer<ReturnType<typeof buildDealEditorFormSchema>>;

interface DealEditorPageProps {
  mode: 'create' | 'edit';
  dealId?: string;
}

const DATETIME_LOCAL_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

interface ParsedDatetimeLocal {
  date: Date;
  hour: string;
  minute: string;
}

function parseDatetimeLocalValue(value: string): ParsedDatetimeLocal | null {
  const match = DATETIME_LOCAL_REGEX.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = match[4] ?? '00';
  const minute = match[5] ?? '00';

  const date = new Date(year, month - 1, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return {
    date,
    hour,
    minute,
  };
}

function toDatetimeLocalValue(date: Date, hour: string, minute: string): string {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function formatDateTimePickerLabel(value: string, t: TFunction): string {
  const parsed = parseDatetimeLocalValue(value);
  if (!parsed) return t('deals.editor.pick_datetime');

  const dateLabel = parsed.date.toLocaleDateString('en-PH', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  return `${dateLabel} ${parsed.hour}:${parsed.minute}`;
}

interface DealDateTimePickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (nextValue: string) => void;
  error?: string;
  errorId: string;
}

function DealDateTimePicker({
  id,
  label,
  value,
  onChange,
  error,
  errorId,
}: DealDateTimePickerProps) {
  const { t } = useTranslation();
  const parsed = parseDatetimeLocalValue(value);
  const selectedDate = parsed?.date;
  const selectedHour = parsed?.hour ?? '00';
  const selectedMinute = parsed?.minute ?? '00';

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className="w-full justify-start font-normal"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
          >
            <CalendarIcon data-icon="inline-start" />
            {formatDateTimePickerLabel(value, t)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={(date) => {
              if (!date) {
                onChange('');
                return;
              }
              onChange(toDatetimeLocalValue(date, selectedHour, selectedMinute));
            }}
          />
          <div className="flex items-center gap-2 border-t p-3">
            <Select
              value={selectedHour}
              onValueChange={(hour) => {
                if (!selectedDate) return;
                onChange(toDatetimeLocalValue(selectedDate, hour, selectedMinute));
              }}
              disabled={!selectedDate}
            >
              <SelectTrigger className="w-[6.5rem]">
                <SelectValue placeholder={t('deals.editor.hour')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {HOUR_OPTIONS.map((hourOption) => (
                    <SelectItem key={hourOption} value={hourOption}>
                      {hourOption}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <span className="text-muted-foreground text-sm">:</span>

            <Select
              value={selectedMinute}
              onValueChange={(minute) => {
                if (!selectedDate) return;
                onChange(toDatetimeLocalValue(selectedDate, selectedHour, minute));
              }}
              disabled={!selectedDate}
            >
              <SelectTrigger className="w-[6.5rem]">
                <SelectValue placeholder={t('deals.editor.minute')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {MINUTE_OPTIONS.map((minuteOption) => (
                    <SelectItem key={minuteOption} value={minuteOption}>
                      {minuteOption}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => onChange('')}
              disabled={!value}
            >
              {t('deals.editor.clear')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {error ? (
        <p id={errorId} className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function toOptionalInteger(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return null;
  return parsed;
}

function getDefaultValues(deal?: Deal): DealEditorFormValues {
  if (!deal) {
    return {
      title: '',
      description: '',
      media: dealMediaToRows(),
      deal_type: 'percent_off',
      discount_value: '',
      fixed_price_centavos: '',
      branch_ids: [],
      starts_at: '',
      ends_at: '',
      product_id: '',
      total_max_uses: '',
      max_uses_per_consumer: '',
    };
  }

  return {
    title: deal.title ?? '',
    description: deal.description ?? '',
    media: dealMediaToRows(deal),
    deal_type: deal.deal_type,
    discount_value: deal.discount_value != null ? String(Number(deal.discount_value)) : '',
    fixed_price_centavos:
      deal.fixed_price_centavos != null ? String(Number(deal.fixed_price_centavos)) : '',
    branch_ids: deal.branch_ids,
    starts_at: manilaIsoToDatetimeLocal(deal.starts_at),
    ends_at: manilaIsoToDatetimeLocal(deal.ends_at),
    product_id: deal.applies_to_product_ids?.[0] ?? '',
    total_max_uses: deal.total_max_uses != null ? String(deal.total_max_uses) : '',
    max_uses_per_consumer:
      deal.max_uses_per_consumer != null ? String(deal.max_uses_per_consumer) : '',
  };
}

function buildDealPayload(
  values: DealEditorFormValues,
): Pick<
  DealCreateInput,
  | 'title'
  | 'description'
  | 'media'
  | 'deal_type'
  | 'discount_value'
  | 'fixed_price_centavos'
  | 'bundle_config'
  | 'branch_ids'
  | 'applies_to_product_ids'
  | 'starts_at'
  | 'ends_at'
  | 'total_max_uses'
  | 'max_uses_per_consumer'
> {
  const startsAtIso = values.starts_at ? datetimeLocalToManilaIso(values.starts_at) : null;
  const endsAtIso = values.ends_at ? datetimeLocalToManilaIso(values.ends_at) : null;

  return {
    title: values.title.trim(),
    description: values.description.trim() || null,
    media: rowsToMediaPayload(values.media),
    deal_type: values.deal_type,
    discount_value: values.deal_type === 'percent_off' ? values.discount_value.trim() : null,
    fixed_price_centavos:
      values.deal_type === 'fixed_price' ? values.fixed_price_centavos.trim() : null,
    bundle_config: values.deal_type === 'bundle' ? {} : null,
    branch_ids: values.branch_ids,
    applies_to_product_ids: values.product_id ? [values.product_id] : null,
    starts_at: startsAtIso,
    ends_at: endsAtIso,
    total_max_uses: toOptionalInteger(values.total_max_uses),
    max_uses_per_consumer: toOptionalInteger(values.max_uses_per_consumer),
  };
}

function DealMediaThumb({ src }: { src: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (failed) {
    return (
      <div
        className="text-muted-foreground bg-muted/40 flex size-16 shrink-0 items-center justify-center rounded-md border border-dashed"
        role="img"
        aria-label={t('deals.editor.media.preview_unavailable')}
        title={t('deals.editor.media.preview_unavailable')}
      >
        <ImageOff className="size-5" aria-hidden />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={t('deals.editor.media.alt')}
      className="size-16 shrink-0 rounded-md border object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function DealMediaGallery({ form }: { form: UseFormReturn<DealEditorFormValues> }) {
  const { t } = useTranslation();
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'media',
  });
  const mediaRows = form.watch('media');
  const atMax = fields.length >= MAX_DEAL_MEDIA;
  const rootError = form.formState.errors.media?.root?.message;
  const hasEmptyRow = (mediaRows ?? []).some((row) => row.url.trim() === '');

  // Write the picked URL exactly where a pasted one would go: the first empty
  // row if there is one, otherwise a new row (the button is disabled at max).
  function applyLibrarySelection(selection: MediaPickerSelection) {
    const emptyIndex = (mediaRows ?? []).findIndex((row) => row.url.trim() === '');
    if (emptyIndex >= 0) {
      form.setValue(`media.${emptyIndex}.url`, selection.url, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    append({ url: selection.url });
  }

  return (
    <div className="space-y-3 md:col-span-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{t('deals.editor.media.label')}</Label>
        <span className="text-muted-foreground text-xs">
          {t('deals.editor.media.counter', { current: fields.length, max: MAX_DEAL_MEDIA })}
        </span>
      </div>
      <p className="text-muted-foreground text-xs">{t('deals.editor.media.help')}</p>

      <div className="space-y-3">
        {fields.map((field, index) => {
          const rowError = form.formState.errors.media?.[index]?.url;
          const trimmedUrl = (mediaRows?.[index]?.url ?? '').trim();
          const showThumb = trimmedUrl.length > 0 && !rowError;
          const inputId = `deal-media-url-${index}`;
          const errorId = `${inputId}-error`;

          return (
            <div key={field.id} className="flex gap-3 rounded-md border p-3">
              {showThumb ? (
                <DealMediaThumb src={trimmedUrl} />
              ) : (
                <div
                  className="text-muted-foreground bg-muted/40 flex size-16 shrink-0 items-center justify-center rounded-md border border-dashed"
                  aria-hidden
                >
                  <ImageIcon className="size-5" />
                </div>
              )}

              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor={inputId} className="text-xs">
                    {t('deals.editor.media.url_label', { index: index + 1 })}
                  </Label>
                  {index === 0 ? (
                    <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                      {t('deals.editor.media.cover')}
                    </span>
                  ) : null}
                </div>
                <Input
                  id={inputId}
                  placeholder="https://..."
                  {...form.register(`media.${index}.url` as const)}
                  aria-invalid={Boolean(rowError)}
                  aria-describedby={rowError ? errorId : undefined}
                />
                {rowError ? (
                  <p id={errorId} className="text-destructive text-sm" role="alert">
                    {rowError.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => move(index, index - 1)}
                  disabled={index === 0}
                  aria-label={t('deals.editor.media.move_up')}
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => move(index, index + 1)}
                  disabled={index === fields.length - 1}
                  aria-label={t('deals.editor.media.move_down')}
                >
                  <ArrowDown />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => remove(index)}
                  aria-label={t('deals.editor.media.remove')}
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {typeof rootError === 'string' ? (
        <p className="text-destructive text-sm" role="alert">
          {rootError}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ url: '' })}
          disabled={atMax}
        >
          <Plus data-icon="inline-start" />
          {t('deals.editor.media.add')}
        </Button>
        <MediaPickerDialog
          onSelect={applyLibrarySelection}
          trigger={
            <Button type="button" variant="outline" size="sm" disabled={atMax && !hasEmptyRow}>
              <Images data-icon="inline-start" />
              {t('deals.editor.media.from_library')}
            </Button>
          }
        />
        {atMax ? (
          <span className="text-muted-foreground text-xs">
            {t('deals.editor.media.max_reached', { max: MAX_DEAL_MEDIA })}
          </span>
        ) : null}
      </div>
    </div>
  );
}

interface DealEditorFormProps {
  mode: 'create' | 'edit';
  deal?: Deal;
}

function DealEditorForm({ mode, deal }: DealEditorFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branchesQuery = useQuery(branchesQueryOptions());
  const branchItems = branchesQuery.data?.items ?? [];

  const dealEditorFormSchema = useMemo(() => buildDealEditorFormSchema(t), [t]);

  const form = useForm<DealEditorFormValues>({
    resolver: zodResolver(dealEditorFormSchema),
    defaultValues: getDefaultValues(deal),
  });

  useEffect(() => {
    form.reset(getDefaultValues(deal));
  }, [deal, form]);

  const selectedDealType = form.watch('deal_type');
  const startsAtValue = form.watch('starts_at');
  const endsAtValue = form.watch('ends_at');
  const selectedProductId = form.watch('product_id');
  const selectedBranchIds = form.watch('branch_ids');

  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productSearchDraft, setProductSearchDraft] = useState('');
  const debouncedProductSearch = useDebounce(productSearchDraft, 250);
  const productQuery = useQuery({
    ...dealProductsQueryOptions({
      q: debouncedProductSearch || undefined,
      page: 1,
      page_size: 10,
    }),
    enabled: productPickerOpen || Boolean(selectedProductId),
  });

  const productItems = productQuery.data?.items ?? [];
  const selectedProduct = productItems.find((item) => item.id === selectedProductId) ?? null;

  const createMutation = useMutation({
    mutationFn: (payload: DealCreateInput) => createDeal(payload),
    onSuccess: () => {
      toast.success(t('deals.editor.toast_created'));
      void queryClient.invalidateQueries({ queryKey: dealsQueryKeyPrefix });
      void navigate({ to: '/deals' });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('deals.editor.toast_create_error')));
    },
  });

  const editMutation = useMutation({
    mutationFn: (payload: DealPatchInput) => patchDeal(deal?.id ?? '', payload),
    onSuccess: () => {
      toast.success(t('deals.editor.toast_updated'));
      void queryClient.invalidateQueries({ queryKey: dealsQueryKeyPrefix });
      void navigate({ to: '/deals' });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('deals.editor.toast_update_error')));
    },
  });

  const isSubmitting = createMutation.isPending || editMutation.isPending;

  function toggleBranch(branchId: string, checked: boolean) {
    const next = checked
      ? [...selectedBranchIds, branchId]
      : selectedBranchIds.filter((id) => id !== branchId);
    form.setValue('branch_ids', next, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const startsAtIso = startsAtValue ? datetimeLocalToManilaIso(startsAtValue) : null;
  const scheduledForFuture = mode === 'create' && resolveCreateStatus(startsAtIso) === 'paused';
  const scheduledDateLabel = formatDateTimePickerLabel(startsAtValue, t);

  function onSubmit(values: DealEditorFormValues) {
    const payload = buildDealPayload(values);

    if (mode === 'create') {
      createMutation.mutate({
        ...payload,
        status: resolveCreateStatus(payload.starts_at),
      });
      return;
    }

    if (!deal) return;
    editMutation.mutate(payload);
  }

  return (
    <>
      {deal ? <DealModerationBanner deal={deal} /> : null}
      <Card>
        <CardHeader>
          <CardTitle>
            {mode === 'create' ? t('deals.editor.card_create') : t('deals.editor.card_edit')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="deal-title">{t('deals.editor.title_label')}</Label>
                <Input
                  id="deal-title"
                  placeholder={t('deals.editor.title_placeholder')}
                  {...form.register('title')}
                  aria-invalid={Boolean(form.formState.errors.title)}
                  aria-describedby="deal-title-error"
                />
                {form.formState.errors.title ? (
                  <p id="deal-title-error" className="text-destructive text-sm" role="alert">
                    {form.formState.errors.title.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="deal-type">{t('deals.editor.deal_type_label')}</Label>
                <Select
                  value={selectedDealType}
                  onValueChange={(value) =>
                    form.setValue('deal_type', value as DealEditorFormValues['deal_type'], {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger id="deal-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent_off">{t('deals.type.percent_off')}</SelectItem>
                    <SelectItem value="fixed_price">{t('deals.type.fixed_price')}</SelectItem>
                    <SelectItem value="bundle">{t('deals.type.bundle')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedDealType === 'percent_off' ? (
                <div className="space-y-2">
                  <Label htmlFor="deal-discount-value">{t('deals.editor.discount_label')}</Label>
                  <Input
                    id="deal-discount-value"
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    placeholder="20"
                    {...form.register('discount_value')}
                    aria-invalid={Boolean(form.formState.errors.discount_value)}
                    aria-describedby="deal-discount-value-error"
                  />
                  {form.formState.errors.discount_value ? (
                    <p
                      id="deal-discount-value-error"
                      className="text-destructive text-sm"
                      role="alert"
                    >
                      {form.formState.errors.discount_value.message}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {selectedDealType === 'fixed_price' ? (
                <div className="space-y-2">
                  <Label htmlFor="deal-fixed-price">{t('deals.editor.fixed_price_label')}</Label>
                  <Input
                    id="deal-fixed-price"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="6000"
                    {...form.register('fixed_price_centavos')}
                    aria-invalid={Boolean(form.formState.errors.fixed_price_centavos)}
                    aria-describedby="deal-fixed-price-error"
                  />
                  {form.formState.errors.fixed_price_centavos ? (
                    <p
                      id="deal-fixed-price-error"
                      className="text-destructive text-sm"
                      role="alert"
                    >
                      {form.formState.errors.fixed_price_centavos.message}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-xs">
                      {t('deals.editor.fixed_price_help')}
                    </p>
                  )}
                </div>
              ) : null}

              {selectedDealType === 'bundle' ? (
                <div className="bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm md:col-span-2">
                  {t('deals.editor.bundle_note')}
                </div>
              ) : null}

              <div className="space-y-2 md:col-span-2">
                <Label>{t('deals.editor.product_label')}</Label>
                <Popover open={productPickerOpen} onOpenChange={setProductPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={productPickerOpen}
                      className="w-full justify-between"
                    >
                      <span className="truncate">
                        {selectedProduct?.display_name ??
                          (selectedProductId
                            ? t('deals.editor.product_selected')
                            : t('deals.editor.product_select'))}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        value={productSearchDraft}
                        onValueChange={setProductSearchDraft}
                        placeholder={t('deals.editor.product_search_placeholder')}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {productQuery.isLoading
                            ? t('deals.editor.product_searching')
                            : t('deals.editor.product_none')}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              form.setValue('product_id', '', {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              setProductPickerOpen(false);
                            }}
                          >
                            {t('deals.editor.product_none_option')}
                            <Check
                              className={cn(
                                'ml-auto h-4 w-4',
                                !selectedProductId ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                          </CommandItem>
                          {productItems.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.id}
                              onSelect={() => {
                                form.setValue('product_id', product.id, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                                setProductPickerOpen(false);
                              }}
                            >
                              <span className="truncate">{product.display_name}</span>
                              <Check
                                className={cn(
                                  'ml-auto h-4 w-4',
                                  selectedProductId === product.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <DealDateTimePicker
                id="deal-starts-at"
                label={t('deals.editor.starts_at_label')}
                value={startsAtValue}
                onChange={(nextValue) =>
                  form.setValue('starts_at', nextValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                error={form.formState.errors.starts_at?.message}
                errorId="deal-starts-at-error"
              />

              <DealDateTimePicker
                id="deal-ends-at"
                label={t('deals.editor.ends_at_label')}
                value={endsAtValue}
                onChange={(nextValue) =>
                  form.setValue('ends_at', nextValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                error={form.formState.errors.ends_at?.message}
                errorId="deal-ends-at-error"
              />

              {scheduledForFuture ? (
                <div
                  data-testid="deal-scheduled-note"
                  className="bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm md:col-span-2"
                >
                  {t('deals.editor.scheduled_note', { date: scheduledDateLabel })}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="deal-total-max-uses">{t('deals.editor.total_max_uses_label')}</Label>
                <Input
                  id="deal-total-max-uses"
                  type="number"
                  min={0}
                  step={1}
                  {...form.register('total_max_uses')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deal-max-uses-per-consumer">
                  {t('deals.editor.max_uses_per_consumer_label')}
                </Label>
                <Input
                  id="deal-max-uses-per-consumer"
                  type="number"
                  min={0}
                  step={1}
                  {...form.register('max_uses_per_consumer')}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="deal-description">{t('deals.editor.description_label')}</Label>
                <Textarea id="deal-description" rows={4} {...form.register('description')} />
              </div>

              <DealMediaGallery form={form} />
            </div>

            <div className="space-y-3">
              <Label>{t('deals.editor.branches_label')}</Label>
              {branchesQuery.isLoading ? (
                <p className="text-muted-foreground text-sm">{t('deals.editor.branches_loading')}</p>
              ) : branchItems.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t('deals.editor.branches_empty')}</p>
              ) : (
                <div className="flex flex-col gap-3 rounded-md border p-3">
                  {branchItems.map((branch) => (
                    <label key={branch.id} className="flex items-start gap-2 text-sm">
                      <Checkbox
                        checked={selectedBranchIds.includes(branch.id)}
                        onCheckedChange={(checked) => toggleBranch(branch.id, checked === true)}
                      />
                      <span className="leading-5">
                        <span className="font-medium">{branch.name}</span>
                        {branch.address ? (
                          <span className="text-muted-foreground"> · {branch.address}</span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {form.formState.errors.branch_ids ? (
                <p className="text-destructive text-sm" role="alert">
                  {form.formState.errors.branch_ids.message}
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" asChild>
                <Link to="/deals">{t('deals.editor.cancel')}</Link>
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? mode === 'create'
                    ? t('deals.editor.submitting_create')
                    : t('deals.editor.submitting_edit')
                  : mode === 'create'
                    ? t('deals.editor.submit_create')
                    : t('deals.editor.submit_edit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}

export function DealEditorPage({ mode, dealId }: DealEditorPageProps) {
  const { t } = useTranslation();
  const dealsQuery = useQuery(dealsQueryOptions({}));
  const deal =
    mode === 'edit'
      ? dealsQuery.data?.items.find((candidate) => candidate.id === dealId)
      : undefined;

  return (
    <>
      <PageHeader
        title={mode === 'create' ? t('deals.editor.page_title_create') : t('deals.editor.page_title_edit')}
        description={
          mode === 'create'
            ? t('deals.editor.page_description_create')
            : t('deals.editor.page_description_edit')
        }
      />

      {mode === 'edit' && dealsQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">{t('deals.editor.loading_detail')}</p>
      ) : mode === 'edit' && dealsQuery.isError ? (
        <QueryErrorState
          error={dealsQuery.error}
          isRetrying={dealsQuery.isRefetching}
          onRetry={dealsQuery.refetch}
        />
      ) : mode === 'edit' && !deal ? (
        <Card>
          <CardContent className="space-y-3 py-8">
            <p className="text-sm font-medium">{t('deals.editor.not_found_title')}</p>
            <p className="text-muted-foreground text-sm">
              {t('deals.editor.not_found_description')}
            </p>
            <div>
              <Button asChild variant="outline" size="sm">
                <Link to="/deals">{t('deals.editor.back_to_deals')}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <DealEditorForm mode={mode} deal={deal} />
      )}
    </>
  );
}
